/**
 * Object Detection Extension for Scratch
 * ========================================
 * Implements all PictoBlox-compatible object detection blocks using
 * @tensorflow-models/coco-ssd (TensorFlow.js COCO-SSD model).
 *
 * Detects 80 object classes from the COCO dataset in real-time
 * via the webcam. Supports bounding box overlay, threshold control,
 * and per-class counting.
 *
 * Block opcodes, field names, menu names, and display text match
 * PictoBlox's .sb3 format exactly.
 */

import * as tf from '@tensorflow/tfjs';
import * as cocoSsd from '@tensorflow-models/coco-ssd';
import formatMessage from 'format-message';

// COCO-SSD 80 object classes
const COCO_CLASSES = [
    'person', 'bicycle', 'car', 'motorcycle', 'airplane', 'bus', 'train',
    'truck', 'boat', 'traffic light', 'fire hydrant', 'stop sign',
    'parking meter', 'bench', 'bird', 'cat', 'dog', 'horse', 'sheep',
    'cow', 'elephant', 'bear', 'zebra', 'giraffe', 'backpack', 'umbrella',
    'handbag', 'tie', 'suitcase', 'frisbee', 'skis', 'snowboard',
    'sports ball', 'kite', 'baseball bat', 'baseball glove', 'skateboard',
    'surfboard', 'tennis racket', 'bottle', 'wine glass', 'cup', 'fork',
    'knife', 'spoon', 'bowl', 'banana', 'apple', 'sandwich', 'orange',
    'broccoli', 'carrot', 'hot dog', 'pizza', 'donut', 'cake', 'chair',
    'couch', 'potted plant', 'bed', 'dining table', 'toilet', 'tv',
    'laptop', 'mouse', 'remote', 'keyboard', 'cell phone', 'microwave',
    'oven', 'toaster', 'sink', 'refrigerator', 'book', 'clock', 'vase',
    'scissors', 'teddy bear', 'hair drier', 'toothbrush'
];

class ObjectDetectionExtension {
    constructor (runtime) {
        this.runtime = runtime;

        // ── Internal state ──────────────────────────────────────
        this._model = null;
        this._modelLoading = false;
        this._detections = []; // coco-ssd prediction results
        this._drawBBox = false;
        this._threshold = 0.5;

        // Video / overlay
        this._video = null;
        this._videoReady = false;
        this._overlayCanvas = null;
        this._overlayCtx = null;

        if (this.runtime) {
            this.runtime.on('PROJECT_STOP_ALL', () => {
                this._stopVideo();
            });
        }
        
        // Trigger model loading immediately upon extension initialization
        this._loadModelInit();
    }

    async _loadModelInit() {
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADING', 'Object Detection');
        }
        await this._loadModel();
        if (this.runtime) {
            this.runtime.emit('MODEL_LOADED', 'Object Detection');
        }
    }

    // ═══════════════════════════════════════════════════════════
    //  getInfo — block definitions matching PictoBlox exactly
    // ═══════════════════════════════════════════════════════════

    _stopVideo () {
        if (this._video && this._video.srcObject) {
            const tracks = this._video.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            this._video.srcObject = null;
        }
        this._videoReady = false;

        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(0, 0, this._overlayCanvas.width, this._overlayCanvas.height);
        }
    }

    getInfo () {
        return {
            id: 'objectDetection',
            name: formatMessage({
                id: 'gui.extension.objectDetection.name',
                default: 'Object Detection'
            }),
            color1: '#D65C5C',
            color2: '#B94A4A',
            blocks: [
                // ── Commands ────────────────────────────────────
                {
                    opcode: 'toggleStageVideoFeed',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'posenet.blocks.turnVideo',
                        default: 'turn [VIDEO_STATE] video on stage with [TRANSPARENCY] % transparency'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: 'string',
                            menu: 'videoStateMenu',
                            defaultValue: 'on'
                        },
                        TRANSPARENCY: {
                            type: 'number',
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'drawBoundingBox',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.boundingBox',
                        default: '[OPTION] bounding box'
                    }),
                    arguments: {
                        OPTION: {
                            type: 'string',
                            menu: 'showHideMenu',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'setThreshold',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.setThreshold',
                        default: 'set detection threshold to [THRESHOLD]'
                    }),
                    arguments: {
                        THRESHOLD: {
                            type: 'string',
                            menu: 'threshold',
                            defaultValue: '0.5'
                        }
                    }
                },
                {
                    opcode: 'analyseImage',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'objectDetection.blocks.analyseImage',
                        default: 'analyse image from [FEED]'
                    }),
                    arguments: {
                        FEED: {
                            type: 'string',
                            menu: 'feedMenu',
                            defaultValue: '1'
                        }
                    }
                },

                // ── Reporters ───────────────────────────────────
                {
                    opcode: 'getObjectCount',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'objectDetection.blocks.getNumObjects',
                        default: 'get # of objects'
                    })
                },
                {
                    opcode: 'getDetectedObject',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'objectDetection.blocks.getDetectedObject',
                        default: 'detected object'
                    })
                },
                {
                    opcode: 'getDetails',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'objectDetection.blocks.getObjectDetail',
                        default: '[OPTION] of object [OBJECT]'
                    }),
                    arguments: {
                        OPTION: {
                            type: 'string',
                            menu: 'detailsMenu',
                            defaultValue: '0'
                        },
                        OBJECT: {
                            type: 'string',
                            menu: 'personNumbers',
                            defaultValue: '1'
                        }
                    }
                },
                {
                    opcode: 'getNoDetected',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'objectDetection.blocks.getNumSpecificObject',
                        default: 'get number of [OBJECT] detected?'
                    }),
                    arguments: {
                        OBJECT: {
                            type: 'string',
                            menu: 'objectClassMenu',
                            defaultValue: 'person'
                        }
                    }
                },

                // ── Booleans ────────────────────────────────────
                {
                    opcode: 'isDetected',
                    blockType: 'Boolean',
                    text: formatMessage({
                        id: 'objectDetection.blocks.isObjectDetected',
                        default: 'is [OBJECT] detected?'
                    }),
                    arguments: {
                        OBJECT: {
                            type: 'string',
                            menu: 'objectClassMenu',
                            defaultValue: 'person'
                        }
                    }
                }
            ],
            menus: {
                // ── Field menus (static dropdowns) ──────────────
                feedMenu: {
                    items: [
                        {text: 'camera', value: '1'},
                        {text: 'stage', value: '2'}
                    ]
                },
                videoStateMenu: {
                    items: ['on', 'off']
                },
                showHideMenu: {
                    items: [
                        {text: 'show', value: '1'},
                        {text: 'hide', value: '0'}
                    ]
                },
                detailsMenu: {
                    items: [
                        {text: 'class', value: '0'},
                        {text: 'x position', value: '1'},
                        {text: 'y position', value: '2'},
                        {text: 'width', value: '3'},
                        {text: 'height', value: '4'}
                    ]
                },
                objectClassMenu: {
                    items: COCO_CLASSES.map(c => ({text: c, value: c}))
                },

                // ── Input menus (shadow blocks) ─────────────────
                // Generates objectDetection_menu_threshold
                threshold: {
                    acceptReporters: true,
                    items: ['0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9']
                },
                // Generates objectDetection_menu_personNumbers
                personNumbers: {
                    acceptReporters: true,
                    items: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
                }
            }
        };
    }

    // ═══════════════════════════════════════════════════════════
    //  Private helpers
    // ═══════════════════════════════════════════════════════════

    /** Load the COCO-SSD model (once). */
    async _loadModel () {
        if (this._model || this._modelLoading) return;
        this._modelLoading = true;
        try {
            await tf.setBackend('webgl');
            await tf.ready();
            this._model = await cocoSsd.load();
            console.log('[ObjectDetection] COCO-SSD model loaded.');
        } catch (err) {
            console.error('[ObjectDetection] Model loading failed:', err);
        }
        this._modelLoading = false;
    }

    /** Create or return the hidden <video> element. */
    async _ensureVideo () {
        if (this._video && this._videoReady) return this._video;
        if (!this._video) {
            this._video = document.createElement('video');
            this._video.setAttribute('autoplay', '');
            this._video.setAttribute('playsinline', '');
            this._video.width = 480;
            this._video.height = 360;
            this._video.style.display = 'none';
            document.body.appendChild(this._video);
        }
        if (!this._videoReady) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {width: 480, height: 360}
                });
                this._video.srcObject = stream;
                await this._video.play();
                this._videoReady = true;
            } catch (err) {
                console.warn('[ObjectDetection] Camera access denied:', err);
            }
        }
        return this._video;
    }

    /** Get or create overlay canvas on top of stage. */
    _ensureOverlay () {
        if (this._overlayCanvas) return this._overlayCanvas;
        const stageCanvas =
            document.querySelector('[class*="stage_stage_"] canvas') ||
            document.querySelector('canvas');
        if (!stageCanvas) return null;
        const parent = stageCanvas.parentElement;
        this._overlayCanvas = document.createElement('canvas');
        this._overlayCanvas.width = stageCanvas.width;
        this._overlayCanvas.height = stageCanvas.height;
        this._overlayCanvas.style.cssText =
            'position:absolute;top:0;left:0;width:100%;height:100%;' +
            'pointer-events:none;z-index:101;';
        parent.style.position = 'relative';
        parent.appendChild(this._overlayCanvas);
        this._overlayCtx = this._overlayCanvas.getContext('2d');
        return this._overlayCanvas;
    }

    _clearOverlay () {
        if (this._overlayCtx && this._overlayCanvas) {
            this._overlayCtx.clearRect(
                0, 0, this._overlayCanvas.width, this._overlayCanvas.height
            );
        }
    }

    _renderOverlay () {
        if (!this._drawBBox) {
            this._clearOverlay();
            return;
        }
        this._ensureOverlay();
        if (!this._overlayCtx) return;
        this._clearOverlay();

        const ctx = this._overlayCtx;
        const cW = this._overlayCanvas.width;
        const cH = this._overlayCanvas.height;
        const srcW = 480;
        const srcH = 360;
        const scaleX = cW / srcW;
        const scaleY = cH / srcH;

        this._detections.forEach(det => {
            const [bx, by, bw, bh] = det.bbox;
            const x = bx * scaleX;
            const y = by * scaleY;
            const w = bw * scaleX;
            const h = bh * scaleY;

            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 2;
            ctx.strokeRect(x, y, w, h);

            const text = `${det.class} (${Math.round(det.score * 100)}%)`;
            ctx.font = '12px sans-serif';
            const tw = ctx.measureText(text).width;
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillRect(x, y - 18, tw + 8, 18);
            ctx.fillStyle = '#00ff00';
            ctx.fillText(text, x + 4, y - 4);
        });
    }

    // ═══════════════════════════════════════════════════════════
    //  Block implementations
    // ═══════════════════════════════════════════════════════════

    async toggleStageVideoFeed (args) {
        const state = args.VIDEO_STATE;
        const transparency = Math.max(0, Math.min(100, Number(args.TRANSPARENCY) || 0));
        try {
            const video = this.runtime.ioDevices.video;
            if (state === 'on') {
                video.enableVideo();
                await this._ensureVideo();
            } else {
                video.disableVideo();
            }
            video.setPreviewGhost(transparency);
        } catch (err) {
            if (state === 'on') await this._ensureVideo();
        }
    }

    drawBoundingBox (args) {
        this._drawBBox = args.OPTION === '1';
        if (!this._drawBBox) this._clearOverlay();
        else this._renderOverlay();
    }

    setThreshold (args) {
        this._threshold = parseFloat(args.THRESHOLD) || 0.5;
    }

    async analyseImage () {
        await this._loadModel();
        const video = await this._ensureVideo();
        if (!video || !this._videoReady || !this._model) {
            this._detections = [];
            return;
        }
        try {
            const predictions = await this._model.detect(video);
            this._detections = predictions.filter(p => p.score >= this._threshold);
        } catch (err) {
            console.warn('[ObjectDetection] Analysis error:', err);
            this._detections = [];
        }
        this._renderOverlay();
    }

    getObjectCount () {
        return this._detections.length;
    }

    getDetectedObject () {
        if (this._detections.length === 0) return '';
        return this._detections[0].class;
    }

    getDetails (args) {
        const idx = (parseInt(args.OBJECT, 10) || 1) - 1;
        const det = this._detections[idx];
        if (!det) return 0;
        switch (args.OPTION) {
        case '0': return det.class;
        case '1': return Math.round(det.bbox[0]); // x
        case '2': return Math.round(det.bbox[1]); // y
        case '3': return Math.round(det.bbox[2]); // width
        case '4': return Math.round(det.bbox[3]); // height
        default: return 0;
        }
    }

    isDetected (args) {
        const target = String(args.OBJECT).toLowerCase();
        return this._detections.some(d => d.class.toLowerCase() === target);
    }

    getNoDetected (args) {
        const target = String(args.OBJECT).toLowerCase();
        return this._detections.filter(d => d.class.toLowerCase() === target).length;
    }
}

export default ObjectDetectionExtension;
